import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as Utils from 'utils';
import {
  assign,
  compose,
  filter,
  map,
  negate,
  pick,
  property,
} from 'lodash/fp';
import { useDispatch } from 'react-redux';
import { CUSTOMER_FORM_ID, NEW_BANK } from '../../../global/constants';
import { SellOrderDetailedApiResponse } from '../../../utils/sell-process/sell-process-detailed-types';
import { renameProperty } from '../../../utils/rename-property';
import { RootState } from '../../../global';
import { omitEmptyStrings } from '../../../utils/omit-empty-strings';
import { AuditorOptionsType } from '../../buy-process-form/components/auditor/auditor-types';
import { renameSsnToPerson } from '../../../utils/person-entities';
import { getSafeISOString } from '../../../utils/dates/get-safe-iso-string';
import {
  BankKind,
  SellOrder,
  SellOrderLegalShareholderStore,
  SellProcessDeleteRequest,
} from './sell-process-types';
import { BasicInfoFormState } from './components/sell-process-form/components/basic-info/basic-info-types';
import { ProcessingFormState } from './components/sell-process-form/components/processing/processing-types';
import { SellRepresentativesFormState } from './components/sell-process-form/components/representatives/representatives-types';
import * as SellProcessUtils from './sell-process-utils';
import {
  OwnersAgreementsFormState,
  SellOwnerRelation,
  SellPersonOwnerStore,
} from './components/sell-process-form/components/owners-agreements/owners-agreements-types';
import { SellBankingFormState } from './components/sell-process-form/components/banking/banking-types';
import { RegistrationOfficeFormState } from './components/sell-process-form/components/registration-office/registration-office-types';
import { WindUpFormState } from './components/sell-process-form/components/wind-up/wind-up-types';
import { SellOrderOrganization } from './sell-process-utils-types';
import {
  isPersonSeller,
  getCbsBank,
  getClientBank,
} from './components/sell-process-form/components/banking/banking-utils';
import { SellOrderAuditorForm } from './components/sell-process-form/components/auditor/auditor-types';
import { SELL_ORDER_DEFAULT } from './sell-process-constants';
import { TaxOfficeFormState } from './components/sell-process-form/components/tax-office/tax-office-types';
import { ShellCompanyDeclarationFormState } from './components/sell-process-form/components/shell-company-declaration/shell-company-declaration-types';
import { ExternalSignatoriesFormState } from './components/sell-process-form/components/external-signatories/external-signatories-types';

type WithId<T> = T & {
  id: string;
};

const sellOrderInitialState: Record<string, SellOrder> = {
  [CUSTOMER_FORM_ID]: {
    id: CUSTOMER_FORM_ID,
    ...SELL_ORDER_DEFAULT,
  },
};

const updateStaffRelation = (
  state: SellOrder,
  userId: string,
  relation:
    | 'businessManager'
    | 'liquidator'
    | 'adjuster'
    | 'signer'
    | 'taxOfficeContactPerson',
) => {
  state[relation] = omitEmptyStrings({
    ...state[relation],
    userId,
  }) as any;
};

export const sellOrderSlice = createSlice({
  name: 'sellOrder',
  initialState: sellOrderInitialState,
  reducers: {
    saveSellOrderProcessing(
      state,
      action: PayloadAction<WithId<ProcessingFormState>>,
    ) {
      const { id, businessManager, ...rest } = action.payload;

      updateStaffRelation(state[id], businessManager.id, 'businessManager');
      state[id] = assign(state[id], rest);
    },

    saveSellOrderBasicInfo(
      state,
      action: PayloadAction<WithId<BasicInfoFormState>>,
    ) {
      const {
        id,
        shareCapital,
        numberShares,
        lowestShareCapital,
        highestShareCapital,
        lowestNumberShares,
        highestNumberShares,
        ...rest
      } = action.payload;

      const metaParameters = {
        shareCapital: Number(shareCapital),
        numberShares: Number(numberShares),
      };

      const companyParameters = {
        ...rest,
        lowestShareCapital: Number(lowestShareCapital),
        highestShareCapital: Number(highestShareCapital),
        lowestNumberShares: Number(lowestNumberShares),
        highestNumberShares: Number(highestNumberShares),
      };

      state[id] = assign(state[id], metaParameters);
      state[id].company = assign(state[id].company, companyParameters);
    },

    saveSellOrderRepresentatives(
      state,
      action: PayloadAction<WithId<SellRepresentativesFormState>>,
    ) {
      const { id, representatives } = action.payload;
      state[id].representatives = map(
        SellProcessUtils.mapSellRepresentativeEntity,
        representatives,
      );
    },

    saveSellOrderOwnersAgreements(
      state,
      action: PayloadAction<WithId<OwnersAgreementsFormState>>,
    ) {
      const { id, sellers, purchasePrice, equity, contractWording } = action.payload;

      state[id] = assign(state[id], {
        purchasePrice,
        equity,
        contractWording,
      });

      state[id].physicalPeople = map(
        SellProcessUtils.mapSellerToSellerEntity,
        filter(isPersonSeller, sellers),
      ) as SellOwnerRelation<SellPersonOwnerStore>[];

      state[id].legalShareholders = map(
        SellProcessUtils.mapSellerToSellerEntity,
        filter(negate(isPersonSeller), sellers),
      ) as SellOwnerRelation<SellOrderLegalShareholderStore>[];
    },

    saveSellOrderBanking(
      state,
      action: PayloadAction<WithId<SellBankingFormState>>,
    ) {
      const {
        id,
        // Omit sellers
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        sellers,
        ...rest
      } = action.payload;

      state[id] = assign(state[id], rest);
      state[id].bank = rest.bank.id;

      const cbsBank = getCbsBank(state[id].banks);
      const clientBank = getClientBank(state[id].banks);
      const isAnnanBankSelected = rest.bank.id === NEW_BANK;
      state[id].banks = [
        {
          id: property('id', cbsBank) as string,
          bankId: rest.clientFundsAccount,
          bankType: BankKind.Internal,
        },
      ];
      if (!isAnnanBankSelected) {
        state[id].banks.push({
          id: property('id', clientBank) as string,
          bankId: rest.bank.id,
          bankAccount: rest.bankAccount,
          bankType: BankKind.Company,
        });
      }
    },

    saveSellOrderRegistrationOffice(
      state,
      action: PayloadAction<WithId<RegistrationOfficeFormState>>,
    ) {
      const {
        id,
        buyerOrganizationNumber,
        buyerName,
        commisionAmount,
        adjuster,
        signer,
        liquidator,
        changedFiscalYear,
        fiscalStartDate,
        fiscalEndDate,
        ...rest
      } = action.payload;

      state[id] = assign(state[id], rest);
      state[id].commisionAmount = Number(commisionAmount);

      updateStaffRelation(state[id], adjuster, 'adjuster');
      updateStaffRelation(state[id], signer, 'signer');
      updateStaffRelation(state[id], liquidator, 'liquidator');

      state[id].changedFiscalYear = changedFiscalYear;
      if (changedFiscalYear) {
        state[id].fiscalStartDate = fiscalStartDate;
        state[id].fiscalEndDate = fiscalEndDate;
      } else {
        delete state[id].fiscalStartDate;
        delete state[id].fiscalEndDate;
      }

      if (rest.isAnotherBuyer) {
        state[id].anotherBuyer = {
          organizationNumber: buyerOrganizationNumber,
          name: buyerName,
        };
      } else {
        delete state[id].anotherBuyer;
      }
    },

    saveSellOrderAuditor(
      state,
      action: PayloadAction<WithId<SellOrderAuditorForm>>,
    ) {
      const { id, auditorFirm, auditorPerson, auditorOption } = action.payload;

      // If there is an auditor firm (has an org-num), but the value is not COMPANY
      // this means that there WAS a company, but now there isn't one, so we set
      // all values to NULL so that backend can know to delete them
      if (auditorOption !== 'company' && auditorFirm.organizationNumber) {
        state[id].auditor = {
          ...state[id].auditor,
          auditorPerson,
          auditorOption,
          auditorFirm: {
            organizationNumber: null as unknown as undefined,
            organizationName: null as unknown as undefined,
            organizationAddress: null as unknown as undefined,
            organizationPostalCode: null as unknown as undefined,
            organizationCity: null as unknown as undefined,
            organizationSigner: false,
          },
        };
      } else {
        state[id].auditor = assign(state[id].auditor, {
          auditorOption,
          auditorFirm: omitEmptyStrings(auditorFirm),
          auditorPerson: omitEmptyStrings(auditorPerson),
        });
      }
    },

    saveSellOrderWindup(state, action: PayloadAction<WithId<WindUpFormState>>) {
      const { id, ...rest } = action.payload;
      state[id] = assign(state[id], rest);
    },
    saveSellOrderTaxOffice(
      state,
      action: PayloadAction<WithId<TaxOfficeFormState>>,
    ) {
      const { id, taxOfficeContactPerson, ...rest } = action.payload;

      state[id] = assign(state[id], rest);
      if (taxOfficeContactPerson) {
        updateStaffRelation(
          state[id],
          taxOfficeContactPerson,
          'taxOfficeContactPerson',
        );
      }
    },
    saveSellOrderShellCompanyDeclaration(
      state,
      action: PayloadAction<WithId<ShellCompanyDeclarationFormState>>,
    ) {
      const { id, ...rest } = action.payload;
      state[id].shellCompanyDeclaration = assign(
        state[id].shellCompanyDeclaration,
        rest,
      );
    },

    saveSellOrderExternalSignatories(
      state,
      action: PayloadAction<WithId<ExternalSignatoriesFormState>>,
    ) {
      const { id, externalSignatories } = action.payload;
      state[id].externalSignatories = (externalSignatories ?? [])
        .filter((signatory) => signatory && signatory.name)
        .map((signatory, index) => ({
          ...signatory,
          position: signatory.position ?? index,
        }));
    },

    deleteSellOrderRelation(
      state,
      action: PayloadAction<WithId<SellProcessDeleteRequest>>,
    ) {
      const { id, relationId, organizationId, signerId } = action.payload;

      if (!state[id].deleteRequest) {
        state[id].deleteRequest = [];
      }

      if (relationId) {
        state[id].deleteRequest?.push({ relationId });
      }

      if (organizationId) {
        state[id].deleteRequest?.push({ organizationId });
      }

      if (signerId) {
        state[id].deleteRequest?.push({ signerId });
      }
    },

    deleteSellOrderStore(state, action: PayloadAction<{ id: string }>) {
      delete state[action.payload.id];
    },

    saveSellOrder(
      state,
      action: PayloadAction<{
        id: string;
        sellOrder: SellOrderDetailedApiResponse;
      }>,
    ) {
      const { sellOrder, id } = action.payload;
      const { relations, auditor, organizations } = sellOrder;

      const mapApiRelationToPointer = compose(
        renameSsnToPerson,
        pick([
          'relationId',
          'relationType',
          'signerId',
          'numberShares',
          'socialSecurityNumber',
          'signer',
        ]),
      ) as () => any;

      const mapOrganizationToPointer = (
        organization: SellOrderOrganization,
      ) => {
        const renameOrgNumToOrganization = renameProperty(
          'number',
          'organization',
        );

        return renameOrgNumToOrganization({
          ...pick(['number', 'numberShares'], organization),
          signers: map(mapApiRelationToPointer, organization.signers),
        });
      };

      const auditorOption = (): AuditorOptionsType => {
        const orgNum = auditor?.organizationNumber;
        const ssn = auditor?.socialSecurityNumber;

        if (orgNum && ssn) {
          return 'company';
        }

        if (ssn && !orgNum) {
          return 'person';
        }

        return 'no';
      };

      const sellMetaData = SellProcessUtils.pickSellMetadata(sellOrder);

      state[id] = {
        representatives: map(
          mapApiRelationToPointer,
          SellProcessUtils.getRepresentativeRelations(relations),
        ),

        physicalPeople: map(
          mapApiRelationToPointer,
          SellProcessUtils.getSellerRelations(relations),
        ),

        legalShareholders: map(
          mapOrganizationToPointer,
          SellProcessUtils.getSellerOrganizations(organizations),
        ),

        // @ts-ignore
        ...sellMetaData,

        commision: sellMetaData.commision == 1,

        company: pick(
          [
            'id',
            'address',
            'postalCode',
            'city',
            'name',
            'number',
            'lowestShareCapital',
            'highestShareCapital',
            'lowestNumberShares',
            'highestNumberShares',
            'businessDescription',
          ],
          sellOrder.company,
        ),

        /*
         * There is 1 CBS bank and 1 Customer bank involved in the deal.
         * The CBS bank is said to be "Internal", so we want the one which
         * is *not* internal.
         * */
        bank: sellOrder.banks.find(
          (bank) => bank.bankType !== BankKind.Internal,
        )?.bankId,

        shellCompanyDeclaration: SellProcessUtils.pickShellCompanyDeclaration(
          sellOrder.shellCompanyDeclaration,
        ),

        externalSignatories: (sellOrder.externalSignatories ?? [])
          .filter((signatory: any) => signatory && signatory.name)
          .map((signatory: any, index: number) => ({
            ...(signatory.id ? { id: signatory.id } : {}),
            name: signatory.name,
            socialSecurityNumber: signatory.socialSecurityNumber,
            address: signatory.address,
            postalCode: signatory.postalCode,
            city: signatory.city,
            position: signatory.position ?? index,
          })),

        auditor: {
          id: property('id', auditor),
          auditorFirm: SellProcessUtils.pickSellAuditorCompany(auditor),
          auditorPerson: Utils.pick(auditor, [
            'firstName',
            'lastName',
            'socialSecurityNumber',
            'address',
            'postalCode',
            'city',
            'signer',
          ]),
          auditorOption: auditorOption(),
        },

        ...SellProcessUtils.getStaffRelations(sellOrder),

        banks: sellOrder.banks.map((bank) =>
          Utils.pick(bank, ['bankAccount', 'bankId', 'bankType', 'id'])),
        changedFiscalYear:
          Boolean(sellMetaData.fiscalStartDate)
          || Boolean(sellMetaData.fiscalEndDate),

        isAnotherBuyer: Boolean(sellMetaData.anotherBuyer),
        auditorReservation: sellMetaData.auditorReservation || false,

        dateDecided: sellOrder.dateDecided || getSafeISOString(),
        dateSent: sellOrder.dateSent || getSafeISOString(),
        dateExtraMeetingProtocol:
          sellOrder.dateExtraMeetingProtocol || getSafeISOString(),
      };
    },

    resetSellOrder(state, action: PayloadAction<{ id: string }>) {
      const { id } = action.payload;
      state[id] = { id, ...SELL_ORDER_DEFAULT };
    },
  },
});

export const selectSellOrder = (id: string) =>
  createSelector(
    (state: RootState) => state.sellOrder[id],
    (res) => res,
  );

export const useDeleteSellOrderRelation = (id: string) => {
  const dispatch = useDispatch();

  return {
    delete: (toDelete: SellProcessDeleteRequest) => {
      dispatch(deleteSellOrderRelation({ id, ...toDelete }));
    },
  };
};

export const sellOrderReducer = sellOrderSlice.reducer;
export const {
  saveSellOrderProcessing,
  saveSellOrderBasicInfo,
  saveSellOrderRepresentatives,
  saveSellOrderOwnersAgreements,
  saveSellOrderBanking,
  saveSellOrderRegistrationOffice,
  saveSellOrderWindup,
  saveSellOrderAuditor,
  saveSellOrderTaxOffice,
  saveSellOrderShellCompanyDeclaration,
  saveSellOrderExternalSignatories,
  saveSellOrder,
  resetSellOrder,

  deleteSellOrderRelation,
  deleteSellOrderStore,
} = sellOrderSlice.actions;
